import api from '@api';
import config from '@config';
import { css, Global } from '@emotion/react';
import { useDebouncedValue, useTouchDevice } from '@hooks';
import AudienceIcon from '@icons/audience.svg?react';
import DeleteIcon from '@icons/close.svg?react';
import SearchIcon from '@icons/search.svg?react';
import { OutlinedButton as Button } from '@ui/buttons';
import { Tooltip } from '@ui/tooltip';
import { getAudienceMetadata, getClassName, log, rem } from '@utils';
import type { ChangeEvent, FocusEvent, KeyboardEvent, MouseEvent } from 'react';
import { memo, useEffect, useRef, useState } from 'react';
import type { Dataset, RelevantAudience } from 'types/schema';
import Styled from './AudienceSelector.styled';

interface Props {
  embedded?: boolean;
  selectedDataset?: Dataset | null;
  selectedAudiences: RelevantAudience[];
  onClose?: () => void;
  onOpenedAudienceMenu: () => void;
  onSearchAudience: () => void;
  onSelectAudience: (audience: RelevantAudience) => void;
}

function AudienceSelector(props: Props) {
  const [audienceInputValue, setAudienceInputValue] = useState('');
  const debouncedAudienceInputValue = useDebouncedValue(
    audienceInputValue,
    500
  );
  const isTouchDevice = useTouchDevice();
  const [foundAudiences, setFoundAudiences] = useState<RelevantAudience[]>([]);
  const [isAudienceMenuOpen, setIsAudienceMenuOpen] = useState(false);
  const [isAudienceMenuLoading, setIsAudienceMenuLoading] = useState(false);
  const abortControllerRef = useRef<AbortController>(null);
  const audienceButtonRef = useRef<HTMLDivElement>(null);
  const audienceInputRef = useRef<HTMLInputElement>(null);
  const audienceMenuRef = useRef<HTMLMenuElement>(null);
  const selectedDatasetId = props.selectedDataset?.id;
  let audienceMenuFocusIndex = -1;

  useEffect(() => {
    if (props.embedded) {
      props.onOpenedAudienceMenu();

      // Focus input only if not a touch device
      if (!isTouchDevice) {
        audienceInputRef.current?.focus();
      }

      return;
    }

    const { current: menuElement } = audienceMenuRef;
    const { current: buttonElement } = audienceButtonRef;

    if (!(menuElement && buttonElement)) {
      return;
    }

    const buttonRect = buttonElement.getBoundingClientRect();

    // Avoid screen overflow by positioning the menu above the input
    if (buttonRect.bottom + config.prompt.menu.maxHeight > window.innerHeight) {
      menuElement.style.setProperty('bottom', 'calc(100% + 8px)');
    } else {
      menuElement.style.setProperty('top', 'calc(100% + 8px)');
    }

    menuElement.querySelector('.static-scrollbar')?.scrollTo(0, 0);
  }, [isTouchDevice, props.embedded, props.onOpenedAudienceMenu]);

  useEffect(() => {
    // Skip if no dataset is selected
    if (!selectedDatasetId) {
      return;
    }

    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    let cachedSuggestedAudiences: RelevantAudience[] = [];

    if (config.release === window.localStorage.getItem('octopus.release')) {
      cachedSuggestedAudiences = JSON.parse(
        window.sessionStorage.getItem('octopus.suggested_audiences') || '[]'
      );
    }

    // Show cached suggested audiences if input is empty
    if (!debouncedAudienceInputValue && cachedSuggestedAudiences.length > 0) {
      setFoundAudiences(cachedSuggestedAudiences);
      setIsAudienceMenuLoading(false);
      return;
    }

    const selectedAudienceIds = props.selectedAudiences.map(
      (audience) => audience.id
    );

    abortControllerRef.current = new AbortController();

    setIsAudienceMenuLoading(true);

    // Capture the current debounced value to avoid stale closures
    const currentSearchTerm = debouncedAudienceInputValue;

    api.audiences
      .search({
        searchTerm: currentSearchTerm,
        currentIds: selectedAudienceIds,
        datasetCode: selectedDatasetId,
        abortSignal: abortControllerRef.current.signal
      })
      .then((audiences) => {
        // Only update if the search term hasn't changed
        if (currentSearchTerm === debouncedAudienceInputValue) {
          if (currentSearchTerm) {
            props.onSearchAudience();
          } else if (cachedSuggestedAudiences.length === 0) {
            window.sessionStorage.setItem(
              'octopus.suggested_audiences',
              JSON.stringify(audiences)
            );
          }

          // Set found audiences
          setFoundAudiences(audiences);
          setIsAudienceMenuLoading(false);
        }
      })
      .catch((error) => {
        // Only handle non-abort errors
        if (error.name !== 'AbortError') {
          console.error('Search failed:', error);
          setIsAudienceMenuLoading(false);
        }
      });

    // Cleanup function to abort pending requests
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [
    debouncedAudienceInputValue,
    selectedDatasetId,
    props.selectedAudiences,
    props.onSearchAudience
  ]);

  const handleAddAudience = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();

    if (isAudienceMenuOpen) {
      setIsAudienceMenuOpen(false);
      return;
    }

    // Show audience menu
    setIsAudienceMenuOpen(true);

    if (!isTouchDevice) {
      // Focus input
      window.setTimeout(() => audienceInputRef.current?.focus(), 0);
    }

    // Callback function
    props.onOpenedAudienceMenu();
  };

  const handleSelectAudience = (index: number) => {
    log('Adding audience to selected audiences.', {
      data: foundAudiences[index]
    });

    setIsAudienceMenuOpen(false);
    setAudienceInputValue('');
    props.onSelectAudience(foundAudiences[index]);
    props.onClose?.();
  };

  const handleAudienceInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    // Update input value
    setAudienceInputValue(event.target.value);

    // Show loading only if input is not empty
    if (event.target.value.trim()) {
      setIsAudienceMenuLoading(true);
    } else {
      // Show cached audiences if input is empty
      setIsAudienceMenuLoading(false);
    }
  };

  const handleAudienceInputKeyDown = (event: KeyboardEvent) => {
    if (event.code === 'Backspace' || event.shiftKey) {
      return;
    }

    const menuElement = audienceMenuRef.current;

    if (event.code === 'Escape') {
      setIsAudienceMenuOpen(false);
      setAudienceInputValue('');
      menuElement?.blur();
      props.onClose?.();
      return;
    }

    const menuItemElements = menuElement?.querySelectorAll<HTMLLIElement>(
      '.audience-menu-item'
    );

    if (event.code === 'Tab') {
      menuItemElements[0]?.focus();
      return;
    }

    if (event.code === 'Enter') {
      menuItemElements[audienceMenuFocusIndex]?.click();
      return;
    }

    menuItemElements[audienceMenuFocusIndex]?.classList.remove('virtual-focus');

    if (event.code === 'ArrowUp') {
      audienceMenuFocusIndex -= 1;
    } else if (event.code === 'ArrowDown') {
      audienceMenuFocusIndex += 1;
    }

    if (audienceMenuFocusIndex < 0) {
      audienceMenuFocusIndex = menuItemElements.length - 1;
    } else if (audienceMenuFocusIndex >= menuItemElements.length) {
      audienceMenuFocusIndex = 0;
    }

    menuItemElements[audienceMenuFocusIndex]?.classList.add('virtual-focus');
    menuItemElements[audienceMenuFocusIndex]?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest'
    });
  };

  const handleBlur = (event: FocusEvent) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setIsAudienceMenuOpen(false);
      setAudienceInputValue('');
    }
  };

  const audienceMenuItems =
    foundAudiences.length > 0 ? (
      foundAudiences.map((audience: RelevantAudience, index: number) => {
        const metadata = getAudienceMetadata(audience.type);

        return (
          <Styled.ListItem
            className="audience-menu-item"
            key={`resolved:${audience.id}`}
            tabIndex={0}
            onClick={() => handleSelectAudience(index)}>
            <Styled.ListItemIcon>{metadata.icon}</Styled.ListItemIcon>
            <Styled.ListItemContent>
              <Styled.ListItemTitle>{audience.title}</Styled.ListItemTitle>
              <Styled.ListItemSubtitle>
                {metadata.description}
              </Styled.ListItemSubtitle>
            </Styled.ListItemContent>
          </Styled.ListItem>
        );
      })
    ) : (
      <Styled.Text>
        <b>No results.</b> Try again or check your Audience name in audiences.
      </Styled.Text>
    );

  let triggerButton = null;

  if (!props.embedded) {
    const { maxSize } = config.prompt.audiences.selected;
    const shouldShowTooltip = props.selectedAudiences.length >= maxSize;

    triggerButton = shouldShowTooltip ? (
      <Tooltip
        className="add-audience-button-tooltip-anchor"
        classesPopper="add-audience-button-tooltip-popper"
        classesTooltip="add-audience-button-tooltip"
        content={
          <>
            You added the maximum number of audiences. Remove one to add
            audiences again.
          </>
        }
        enterDelay={200}
        enterTouchDelay={200}
        leaveDelay={0}
        placement="bottom-start">
        <div>
          <Button
            className="button"
            disabled
            startIcon={<AudienceIcon />}
            testId="chat-add-audience-button">
            Add audience
          </Button>
        </div>
      </Tooltip>
    ) : (
      <div ref={audienceButtonRef}>
        <Button
          className={getClassName({
            ['button']: true,
            ['selected']: isAudienceMenuOpen
          })}
          startIcon={<AudienceIcon />}
          tabIndex={0}
          testId="chat-add-audience-button"
          onClick={handleAddAudience}>
          Add audience
        </Button>
      </div>
    );
  }

  const audienceInput = (
    <Styled.SearchInputWrapper>
      <SearchIcon className="icon" />
      <Styled.SearchInput
        data-ignore-global-keystroke
        data-testid="chat-audience-input"
        maxLength={config.prompt.audiences.input.maxLength}
        ref={audienceInputRef}
        placeholder="Search"
        value={audienceInputValue}
        onChange={handleAudienceInputChange}
        onFocus={() => setIsAudienceMenuOpen(true)}
        onKeyDown={handleAudienceInputKeyDown}
      />
      {audienceInputValue && (
        <Styled.ResetButton onClick={() => setAudienceInputValue('')}>
          <DeleteIcon className="icon" />
        </Styled.ResetButton>
      )}
    </Styled.SearchInputWrapper>
  );

  const audienceMenu = (
    <Styled.Menu
      ref={audienceMenuRef}
      data-testid="suggested-audiences-menu"
      embedded={props.embedded}
      hidden={!props.embedded && !isAudienceMenuOpen}>
      {audienceInput}
      <Styled.MenuLabel data-testid="suggested-audiences-menu-label">
        {audienceInputValue === '' ? 'Suggested audiences' : 'Audience results'}
      </Styled.MenuLabel>
      <Styled.List
        className="static-scrollbar"
        scrollable={!isAudienceMenuLoading}
        alwaysShowScrollbar={foundAudiences.length > 4}>
        {isAudienceMenuLoading
          ? Array.from({ length: 4 }).map((_, index) => (
              <Styled.ListItemSkeleton
                className="fade-in-out"
                key={`list-item-skeleton:${index}`}>
                <Styled.ListItemContent />
              </Styled.ListItemSkeleton>
            ))
          : audienceMenuItems}
      </Styled.List>
    </Styled.Menu>
  );

  return (
    <Styled.Wrapper onBlur={handleBlur}>
      <Global styles={styles} />
      {triggerButton}
      {audienceMenu}
    </Styled.Wrapper>
  );
}

const styles = css`
  .add-audience-button-tooltip-anchor {
    cursor: default;
  }

  .add-audience-button-tooltip {
    && {
      line-height: ${rem(16)};
      margin: ${rem(6)} 0 !important;
    }
  }

  .add-audience-button-tooltip-popper {
    && {
      max-width: 264px;
    }
  }
`;

export default memo(AudienceSelector);
